import { Component, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { SidebarService } from "../../../../_services/_sidebar/sidebar.service";
import { ModulePermissionService } from "../../../../_services/_permissions/module-permission.service";

export interface IMenuItem {
  children?: IMenuItem[];
  icon?: string;
  label: string;
  module?: string;
  path?: string;
  roles: string[];
  type?: string;
}

@Component({
  selector: "lokos-side-menu",
  templateUrl: "./side-menu.component.html",
  styleUrl: "./side-menu.component.scss",
  standalone: false,
})
export class SideMenuComponent implements OnInit {
  expandedKey: string | null = null;
  currentUrl = "";

  flyoutItem: IMenuItem | null = null;
  flyoutTop = 0;
  private flyoutHideTimer: ReturnType<typeof setTimeout> | null = null;

  baseMenuList: IMenuItem[] = [
    {
      type: "link",
      label: "dashboard",
      icon: "assets/images/dashboard.svg",
      path: "/main/dashboard",
      roles: ["all"],
    },
    {
      type: "sub",
      label: "userMapping",
      icon: "assets/images/userMapping.svg",
      roles: ["all"],
      children: [
        {
          label: "clfMapping",
          path: "/main/user-mapping/clf-mapping",
          module: "user-mapping",
          roles: ["all"],
        },
      ],
    },
    {
      type: "sub",
      label: "inputs",
      icon: "assets/images/clf-activity.svg",
      roles: ["all"],
      children: [
        {
          label: "profiles",
          path: "/main/inputs/profiles",
          module: "basic-details",
          roles: ["all"],
        },
        {
          label: "cutOff",
          path: "/main/inputs/cut-off",
          roles: ["all"],
        },
        {
          label: "monthlyUpdate",
          path: "/main/inputs/monthly-update",
          roles: ["all"],
        },
        {
          label: "quarterlyUpdate",
          path: "/main/inputs/quarterly-update",
          roles: ["all"],
        },
        {
          label: "annualUpdate",
          path: "/main/inputs/annual-update",
          roles: ["all"],
        },
      ],
    },
    {
      type: "link",
      label: "outputs",
      icon: "assets/images/outputs.svg",
      path: "/main/outputs",
      roles: ["all"],
    },
    {
      type: "sub",
      label: "master",
      icon: "assets/images/inputs.svg",
      roles: ["all"],
      children: [
        {
          label: "clf",
          path: "/main/master/clf",
          module: "clf-master",
          roles: ["all"],
        },
      ],
    },
  ];

  constructor(
    private router: Router,
    public sidebar: SidebarService,
    private modulePermission: ModulePermissionService,
  ) {}

  ngOnInit(): void {
    this.baseMenuList = this.filterByPermissions(this.baseMenuList);

    this.currentUrl = this.router.url;
    this.autoExpandActiveParent();
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.currentUrl = e.urlAfterRedirects;
        this.autoExpandActiveParent();
      });
  }

  private filterByPermissions(items: IMenuItem[]): IMenuItem[] {
    return items
      .map((item) => {
        if (item.children?.length) {
          return {
            ...item,
            children: item.children.filter(
              (child) =>
                !child.module || this.modulePermission.canView(child.module),
            ),
          };
        }
        return item;
      })
      .filter((item) => {
        if (item.children) {
          return item.children.length > 0;
        }
        return !item.module || this.modulePermission.canView(item.module);
      });
  }

  autoExpandActiveParent() {
    for (const item of this.baseMenuList) {
      if (item.children?.length) {
        const hasActiveChild = item.children.some((c) =>
          c.path ? this.currentUrl.startsWith(c.path) : false,
        );
        if (hasActiveChild) {
          this.expandedKey = item.label;
          return;
        }
      }
    }
  }

  onParentClick(item: IMenuItem, ev: MouseEvent) {
    if (this.sidebar.collapsed && item.children?.length) {
      this.openFlyout(item, ev);
      return;
    }
    if (item.children?.length) {
      this.expandedKey = this.expandedKey === item.label ? null : item.label;
    } else if (item.path) {
      this.router.navigateByUrl(item.path);
    }
  }

  openFlyout(item: IMenuItem, ev: MouseEvent) {
    if (!this.sidebar.collapsed || !item.children?.length) return;
    if (this.flyoutHideTimer) {
      clearTimeout(this.flyoutHideTimer);
      this.flyoutHideTimer = null;
    }
    const target = ev.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.flyoutTop = rect.top;
    this.flyoutItem = item;
  }

  keepFlyout() {
    if (this.flyoutHideTimer) {
      clearTimeout(this.flyoutHideTimer);
      this.flyoutHideTimer = null;
    }
  }

  scheduleCloseFlyout() {
    this.flyoutHideTimer = setTimeout(() => {
      this.flyoutItem = null;
      this.flyoutHideTimer = null;
    }, 160);
  }

  closeFlyout() {
    if (this.flyoutHideTimer) {
      clearTimeout(this.flyoutHideTimer);
      this.flyoutHideTimer = null;
    }
    this.flyoutItem = null;
  }

  isExpanded(item: IMenuItem): boolean {
    return this.expandedKey === item.label;
  }

  isItemActive(item: IMenuItem): boolean {
    if (!item.path) return false;
    return (
      this.currentUrl === item.path ||
      this.currentUrl.startsWith(item.path + "/")
    );
  }

  isParentActive(item: IMenuItem): boolean {
    if (!item.children?.length) return false;
    return item.children.some(
      (c) => c.path && this.currentUrl.startsWith(c.path),
    );
  }

  isChildActive(child: IMenuItem): boolean {
    if (!child.path) return false;
    return (
      this.currentUrl === child.path ||
      this.currentUrl.startsWith(child.path + "/")
    );
  }
}
